export type ActiveMentionQuery = {
  endOffset: number;
  query: string;
  startOffset: number;
};

type MentionTargetText = {
  mentionAliases?: string[];
  mentionText: string;
  searchTerms: string[];
};

export type MentionTarget = MentionTargetText &
  (
    | {
        id: "all";
        kind: "all";
        mentionText: "all";
      }
    | {
        id: string;
        kind: "breakout";
        number: number;
      }
    | {
        displayName: string;
        email: string;
        id: string;
        initials: string;
        kind: "user";
        userId: string;
      }
  );

export type MentionRange = {
  endOffset: number;
  startOffset: number;
};

// A mention ends before whitespace or Unicode punctuation, except connector punctuation
// such as `_`, so `@all_students` does not match `@all`.
// Pd = dash, Pe = close, Pf = final quote, Pi = initial quote,
// Po = other, and Ps = open. Pc (connector punctuation) is intentionally excluded.
const MENTION_END_BOUNDARY = /[\s\p{Pd}\p{Pe}\p{Pf}\p{Pi}\p{Po}\p{Ps}]/u;
const SKIP_MENTION_DECORATION_SELECTOR = "a, code, pre";

const isMentionStartBoundary = (character: string | undefined) =>
  character === undefined || /\s/.test(character);

const isMentionEndBoundary = (character: string | undefined) =>
  character === undefined || MENTION_END_BOUNDARY.test(character);

export const getActiveMentionQuery = (
  text: string,
  selectionStart: number,
  selectionEnd = selectionStart,
): ActiveMentionQuery | null => {
  if (selectionStart !== selectionEnd || selectionStart < 0 || selectionStart > text.length) {
    return null;
  }

  for (let index = selectionStart - 1; index >= 0; index -= 1) {
    const isMentionTrigger = text[index] === "@" && isMentionStartBoundary(text[index - 1]);
    if (isMentionTrigger) {
      const query = text.slice(index + 1, selectionStart);
      if (query.includes("\n") || query.includes("@")) return null;

      return { startOffset: index, endOffset: selectionStart, query };
    }
  }

  return null;
};

export const filterMentionTargets = (targets: MentionTarget[], query: string): MentionTarget[] => {
  const normalizedQuery = query.toLocaleLowerCase();
  return targets.filter((target) =>
    target.searchTerms.some((term) => term.toLocaleLowerCase().startsWith(normalizedQuery)),
  );
};

export const replaceActiveMentionQuery = (
  text: string,
  activeQuery: ActiveMentionQuery,
  mentionText: string,
): { cursorPosition: number; text: string } => {
  const before = text.slice(0, activeQuery.startOffset);
  const untouchedAfter = text.slice(activeQuery.endOffset);
  const followingCharacter = untouchedAfter[0];
  const reusesExistingSpace = followingCharacter === " ";
  const needsSeparator =
    followingCharacter === undefined || !isMentionEndBoundary(followingCharacter);
  const separator = reusesExistingSpace || needsSeparator ? " " : "";
  const after = reusesExistingSpace ? untouchedAfter.slice(1) : untouchedAfter;
  const mention = `@${mentionText}${separator}`;

  return {
    cursorPosition: before.length + mention.length,
    text: `${before}${mention}${after}`,
  };
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const findMentionRanges = (text: string, mentionTexts: string[]): MentionRange[] => {
  const ranges = mentionTexts.flatMap((mentionText) => {
    if (!mentionText) return [];

    const matches: MentionRange[] = [];
    const mentionPattern = new RegExp(`@${escapeRegExp(mentionText)}`, "gi");
    let match = mentionPattern.exec(text);

    while (match) {
      const startOffset = match.index;
      const endOffset = startOffset + match[0].length;
      const precedingCharacter = text[startOffset - 1];
      const followingCharacter = text[endOffset];
      const validStart = isMentionStartBoundary(precedingCharacter);
      const validEnd = isMentionEndBoundary(followingCharacter);

      if (validStart && validEnd) {
        matches.push({ startOffset, endOffset });
      }

      match = mentionPattern.exec(text);
    }

    return matches;
  });

  return ranges
    .sort((a, b) => a.startOffset - b.startOffset || b.endOffset - a.endOffset)
    .reduce<MentionRange[]>((nonOverlappingRanges, range) => {
      const previousRange = nonOverlappingRanges.at(-1);
      if (!previousRange || range.startOffset >= previousRange.endOffset) {
        nonOverlappingRanges.push(range);
      }
      return nonOverlappingRanges;
    }, []);
};

export const containsMention = (text: string, mentionText: string) =>
  findMentionRanges(text, [mentionText]).length > 0;

export const getMentionTexts = (target: MentionTarget) => [
  target.mentionText,
  ...(target.mentionAliases ?? []),
];

export const getDecoratableTextNodes = (root: DocumentFragment): Text[] => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    const textNode = currentNode as Text;
    if (!textNode.parentElement?.closest(SKIP_MENTION_DECORATION_SELECTOR)) {
      textNodes.push(textNode);
    }
    currentNode = walker.nextNode();
  }

  return textNodes;
};

export const getMentionTooltipId = (scopeId: string, targetId: string) =>
  `chat-mention-tooltip-${scopeId}-${targetId}`.replace(/[^a-zA-Z0-9_-]/g, "-");

export const decorateMentionsInHtml = (
  html: string,
  targets: MentionTarget[],
  tooltipScopeId?: string,
): string => {
  if (!targets.length || typeof document === "undefined") return html;

  const template = document.createElement("template");
  template.innerHTML = html;
  const targetsByMentionText = new Map(
    targets.flatMap((target) =>
      getMentionTexts(target).map((mentionText) => [mentionText.toLocaleLowerCase(), target]),
    ),
  );
  const mentionTexts = targets.flatMap(getMentionTexts);

  for (const textNode of getDecoratableTextNodes(template.content)) {
    const ranges = findMentionRanges(textNode.data, mentionTexts);
    if (ranges.length) {
      const fragment = document.createDocumentFragment();
      let cursor = 0;

      for (const range of ranges) {
        fragment.append(textNode.data.slice(cursor, range.startOffset));
        const mention = document.createElement("strong");
        const mentionText = textNode.data.slice(range.startOffset + 1, range.endOffset);
        const target = targetsByMentionText.get(mentionText.toLocaleLowerCase());
        mention.className =
          "-ml-1 rounded-[4px] p-1 font-semibold text-blue-shade-20 hover:bg-blue-tint-90 focus:bg-blue-tint-90 focus:outline-none supports-squircle:rounded-[8px] supports-squircle:corner-squircle";
        mention.dataset.chatMention = "true";
        mention.textContent = textNode.data.slice(range.startOffset, range.endOffset);
        if (target?.kind === "user" && tooltipScopeId) {
          const tooltipId = getMentionTooltipId(tooltipScopeId, target.id);
          mention.setAttribute("aria-describedby", tooltipId);
          mention.dataset.chatMentionTargetId = target.id;
          mention.dataset.chatMentionTooltipScope = tooltipScopeId;
          mention.dataset.tooltipId = tooltipId;
          mention.tabIndex = 0;
        }
        fragment.append(mention);
        cursor = range.endOffset;
      }

      fragment.append(textNode.data.slice(cursor));
      textNode.replaceWith(fragment);
    }
  }

  return template.innerHTML;
};
